import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import './MainScreen.css'
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useServices } from '../../hooks/useServices'
import useStableVh from '../../hooks/useStableVh'
import mainImage1 from "../../asset/main-slider/main_1.avif"

const EXIT_DURATION = 280
const ENTER_DURATION = 520
const AUTO_PLAY_DELAY = 6500

const MainScreen = () => {
  useStableVh()
  const navigate = useNavigate()
  const { data: apiServices = [] } = useServices()

  const mainSlides = apiServices.map((service) => ({
    slug: service.slug,
    title: service.title.toUpperCase(),
    description: service.description,
    image: service.heroImage || mainImage1,
  }))

  const [currentIndex, setCurrentIndex] = useState(0)
  const [previousIndex, setPreviousIndex] = useState(0)
  const [animationPhase, setAnimationPhase] = useState("idle")
  const [isBackgroundTransitioning, setIsBackgroundTransitioning] = useState(false)
  const [direction, setDirection] = useState("next")
  const [loadedImages, setLoadedImages] = useState(() => new Set())
  const transitionTimersRef = useRef([])
  const autoPlayTimerRef = useRef(null)

  const slideImageUrls = useMemo(
    () => Array.from(new Set(mainSlides.map((slide) => slide.image))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiServices]
  )

  useEffect(() => {
    let cancelled = false

    slideImageUrls.forEach((src) => {
      const preloadImg = new Image()
      const markLoaded = () => {
        if (cancelled) return
        setLoadedImages((prev) => {
          if (prev.has(src)) return prev
          const next = new Set(prev)
          next.add(src)
          return next
        })
      }
      preloadImg.onload = markLoaded
      preloadImg.onerror = markLoaded
      preloadImg.src = src
      if (preloadImg.complete) markLoaded()
    })

    return () => {
      cancelled = true
    }
  }, [slideImageUrls])

  const clearTransitionTimers = () => {
    transitionTimersRef.current.forEach((timerId) => window.clearTimeout(timerId))
    transitionTimersRef.current = []
  }

  const clearAutoPlayTimer = () => {
    if (autoPlayTimerRef.current) {
      window.clearTimeout(autoPlayTimerRef.current)
      autoPlayTimerRef.current = null
    }
  }

  const isAnimating = animationPhase !== "idle"

  const safeIndex = (i) => (mainSlides.length ? ((i % mainSlides.length) + mainSlides.length) % mainSlides.length : 0)
  const currentService = mainSlides[safeIndex(currentIndex)] || { title: '', description: '', image: mainImage1, slug: '' }
  const previousService = mainSlides[safeIndex(previousIndex)] || currentService
  const isCurrentImageLoaded = loadedImages.has(currentService.image)

  const switchSlide = useCallback((step) => {
    if (isAnimating || mainSlides.length < 2) return
    clearTransitionTimers()
    clearAutoPlayTimer()
    const nextDirection = step > 0 ? "next" : "prev"
    const nextIndex = safeIndex(currentIndex + step)
    setPreviousIndex(currentIndex)
    setDirection(nextDirection)
    setAnimationPhase("out")

    const exitTimer = window.setTimeout(() => {
      setCurrentIndex(nextIndex)
      setIsBackgroundTransitioning(true)
      setAnimationPhase("in")

      const enterTimer = window.setTimeout(() => {
        setAnimationPhase("idle")
        setIsBackgroundTransitioning(false)
      }, ENTER_DURATION)

      transitionTimersRef.current.push(enterTimer)
    }, EXIT_DURATION)

    transitionTimersRef.current.push(exitTimer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, isAnimating, mainSlides.length])

  useEffect(() => {
    if (animationPhase !== "idle" || mainSlides.length < 2) return undefined
    clearAutoPlayTimer()
    autoPlayTimerRef.current = window.setTimeout(() => switchSlide(1), AUTO_PLAY_DELAY)
    return () => clearAutoPlayTimer()
  }, [currentIndex, animationPhase, switchSlide, mainSlides.length])

  useEffect(() => {
    return () => {
      clearTransitionTimers()
      clearAutoPlayTimer()
    }
  }, [])

  const innerAnimationClass = [
    animationPhase === "out" ? "is-exiting" : "",
    animationPhase === "in" ? "is-entering" : "",
    direction === "next" ? "to-next" : "to-prev",
  ].filter(Boolean).join(" ")

  const handleChooseService = () => {
    if (!currentService?.slug) return
    navigate(`/services/${currentService.slug}`)
  }

  return (
    <div
      className={`main-screen ${isBackgroundTransitioning ? "is-bg-transitioning" : ""}`}
      style={{
        '--main-bg-current': `url(${currentService.image})`,
        '--main-bg-previous': `url(${previousService.image})`,
        position: 'relative',
      }}
    >
      <div
        className={`main-screen-skeleton img-skeleton ${isCurrentImageLoaded ? "is-hidden" : ""}`}
        aria-hidden="true"
      />
      <div className="main-screen-overlay" />
      <div className={`main-screen-inner ${innerAnimationClass}`}>
        <div className="main-text-wrapper">
          <h1 className="main-text-big main-animated main-animated-title">{currentService.title}</h1>
          <p className="main-text-small main-animated main-animated-description">{currentService.description}</p>
        </div>
        <div className="main-controls-wrapper">
          <span className="main-slide-indicator main-animated main-animated-indicator">
            <span className="main-slide-current">{mainSlides.length ? currentIndex + 1 : 0}</span>
            <span className="main-slide-separator">/</span>
            <span className="main-slide-total">{mainSlides.length}</span>
          </span>
          <div className="main-nav-buttons main-animated main-animated-nav">
            <button
              type="button"
              className="main-nav-btn"
              aria-label="Предыдущий слайд"
              onClick={() => switchSlide(-1)}
              disabled={isAnimating}
            >
              <ChevronLeft size={24} strokeWidth={2} />
            </button>
            <button
              type="button"
              className="main-nav-btn"
              aria-label="Следующий слайд"
              onClick={() => switchSlide(1)}
              disabled={isAnimating}
            >
              <ChevronRight size={24} strokeWidth={2} />
            </button>
          </div>
          <button
            type="button"
            className="main-cta-button main-animated main-animated-cta"
            onClick={handleChooseService}
          >
            <span>Выбрать услугу</span>
            <ArrowRight size={20} strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  )
}

export default MainScreen
